import { useMemo } from 'react';
import {
  formatForDateTimeInput,
  parseFromDateTimeInput,
  formatForDisplay,
  getTimelineStatusInfo,
  isOverdue,
  getRelativeTime,
} from '../../../utils/timezoneUtils';

export const getStatusColor = (status) => {
  const colorMap = {
    not_started: 'text-slate-400',
    in_progress: 'text-blue-400',
    on_hold: 'text-amber-400',
    completed: 'text-green-400',
    cancelled: 'text-red-400',
  };
  return colorMap[status] || 'text-slate-400';
};

export const getStatusBadgeColor = (status) => {
  const colorMap = {
    not_started: 'bg-slate-700/50 border-slate-600 text-slate-300',
    in_progress: 'bg-blue-700/50 border-blue-600 text-blue-300',
    on_hold: 'bg-amber-700/50 border-amber-600 text-amber-300',
    completed: 'bg-green-700/50 border-green-600 text-green-300',
    cancelled: 'bg-red-700/50 border-red-600 text-red-300',
  };
  return colorMap[status] || 'bg-slate-700/50 border-slate-600 text-slate-300';
};

export const getPriorityBadgeColor = (priority) => {
  const colorMap = {
    low: 'bg-slate-700/50 border-slate-600 text-slate-300',
    medium: 'bg-blue-700/50 border-blue-600 text-blue-300',
    high: 'bg-orange-700/50 border-orange-600 text-orange-300',
    critical: 'bg-red-700/50 border-red-600 text-red-300',
  };
  return colorMap[priority] || 'bg-slate-700/50 border-slate-600 text-slate-300';
};

export const getAvailableStatusOptions = (project) => {
  return ['not_started', 'in_progress', 'on_hold', 'completed', 'cancelled'];
};

export const useTimelineCalculations = (projects) => {
  return useMemo(() => {
    if (!projects || projects.length === 0) {
      return {
        totalProjects: 0,
        completedProjects: 0,
        inProgressProjects: 0,
        onHoldProjects: 0,
        overdueProjects: 0,
        upcomingDeadlines: [],
      };
    }

    const now = new Date();
    const upcomingDeadlines = [];

    projects.forEach((project) => {
      if (project.end_datetime && project.status !== 'completed' && project.status !== 'cancelled') {
        const endDate = parseFromDateTimeInput(project.end_datetime);
        const daysUntilEnd = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
        if (daysUntilEnd <= 7 && daysUntilEnd >= 0) {
          upcomingDeadlines.push({
            project: project.name,
            days: daysUntilEnd,
            date: endDate,
          });
        }
      }
    });

    upcomingDeadlines.sort((a, b) => a.days - b.days);

    return {
      totalProjects: projects.length,
      completedProjects: projects.filter((p) => p.status === 'completed').length,
      inProgressProjects: projects.filter((p) => p.status === 'in_progress').length,
      onHoldProjects: projects.filter((p) => p.status === 'on_hold').length,
      overdueProjects: projects.filter((p) => p.end_datetime && isOverdue(p.end_datetime, p.status)).length,
      upcomingDeadlines: upcomingDeadlines.slice(0, 5),
    };
  }, [projects]);
};

export const useFilteredProjects = (projects, searchTerm, filterStatus) => {
  return useMemo(() => {
    return projects.filter((project) => {
      const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [projects, searchTerm, filterStatus]);
};

export const showNotification = (setNotification, message, type = 'error') => {
  setNotification({ message, type });
  setTimeout(() => setNotification(null), 5000);
};

export const addTaskReference = (setTaskReference, setNewNote, taskTitle) => {
  const reference = `@Task: ${taskTitle} - `;
  setTaskReference(reference);
  setNewNote((prev) => prev + reference);
  setTimeout(() => {
    document.querySelector('#notes-textarea')?.focus();
  }, 100);
};

export const toggleTaskExpanded = (setExpandedTasks, taskId) => {
  setExpandedTasks((prev) => ({
    ...prev,
    [taskId]: !prev[taskId],
  }));
};

export const handleStatusChange = async (
  selectedProject,
  newStatus,
  updateProject,
  setSelectedProject,
  setShowStatusDropdown,
  showNotification,
  socket
) => {
  try {
    const updatedProject = await updateProject(selectedProject.id, { status: newStatus });
    setSelectedProject(updatedProject);
    setShowStatusDropdown(false);
    
    if (socket) {
      socket.emit('project-tracker-update', {
        type: 'project',
        action: 'status_change',
        data: updatedProject,
        projectId: selectedProject.id,
        newStatus,
      });
    }
    
    showNotification(`Project status updated to ${newStatus.replace('_', ' ')}`, 'success');
  } catch (error) {
    showNotification('Failed to update project status', 'error');
  }
};

export const handlePriorityChange = async (
  selectedProject,
  newPriority,
  updateProject,
  setSelectedProject,
  setShowPriorityDropdown,
  showNotification,
  socket
) => {
  try {
    const updatedProject = await updateProject(selectedProject.id, { 
      name: selectedProject.name,
      description: selectedProject.description || '',
      priority: newPriority 
    });
    if (setSelectedProject) setSelectedProject(updatedProject);
    if (setShowPriorityDropdown) setShowPriorityDropdown(false);
    
    if (socket) {
      socket.emit('project-tracker-update', {
        type: 'project',
        action: 'priority_change',
        data: updatedProject,
        projectId: selectedProject.id,
        newPriority,
      });
    }
    
    showNotification(`Project priority updated to ${newPriority}`, 'success');
  } catch (error) {
    showNotification('Failed to update project priority', 'error');
  }
};